import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Booking } from '../../data/bookings';
import {
  CancelReason,
  feeFor,
  refundAmount,
  reasonsFor,
  windowFor,
} from '../../data/cancellation';
import {
  CancellationPolicy,
  fetchCancellationPolicy,
} from '../../services/bookings';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * Cancelling, with the consequence stated before the confirm.
 *
 * ---------------------------------------------------------------------------
 * THE SHEET IS DIFFERENT IN EVERY WINDOW
 * ---------------------------------------------------------------------------
 * Which one it is comes from `windowFor(booking.stage)`, and it changes the
 * title, the money panel, the reasons offered and the button. That is the
 * whole point: "Cancel booking" that quietly charges a fee, or that promises a
 * full refund on a job already half done, is how a cancellation becomes a
 * dispute.
 *
 * The hardest case is window E - work has started. There is no honest number
 * to show, so the panel shows a sentence and the button says "Request to stop
 * work" rather than "Cancel". Quoting a percentage a person will later
 * override is worse than admitting a person decides.
 *
 * ---------------------------------------------------------------------------
 * THE REASON IS ASKED FOR, NEVER REQUIRED
 * ---------------------------------------------------------------------------
 * A confirm button that stays dead until a radio is picked gets pressed twice
 * as often by somebody who has already given up on the flow. The reasons are
 * offered, the button works without one.
 */

type Props = {
  /** Null while the sheet animates closed. */
  booking: Booking | null;
  onClose: () => void;
  onConfirm: (booking: Booking, reason?: CancelReason) => void;
};

/**
 * Rupees, grouped - `Intl` is not reliably present on Android JSC. Paise are
 * shown only when there are some: the server's 25% of ₹99 is ₹24.75.
 */
const rupees = (value: number) => {
  const [whole, paise] = (
    Number.isInteger(value) ? String(value) : value.toFixed(2)
  ).split('.');
  return (
    '₹' +
    whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
    (paise ? `.${paise}` : '')
  );
};

function CancelSheet({ booking, onClose, onConfirm }: Props) {
  const [reason, setReason] = useState<CancelReason | null>(null);
  /*
   * The server's figures for this cancellation, once they arrive.
   *
   * The app's own `windowFor`/`feeFor` estimate is what shows until then (and
   * if the call fails), but it is only an estimate: it knew nothing of the
   * flat fee when a Pro is already on the way, of a plan that waives the fee,
   * or of paise - on a ₹99 job with the Pro en route it promised ₹74 back and
   * the server refunded ₹49. What is quoted must be what is charged.
   */
  const [policy, setPolicy] = useState<CancellationPolicy | null>(null);

  // A reason picked for one booking must not be carried to the next.
  useEffect(() => {
    if (booking) {
      setReason(null);
    }
  }, [booking]);

  const bookingId = booking?.id;
  useEffect(() => {
    setPolicy(null);
    if (!bookingId) return undefined;
    const controller = new AbortController();
    fetchCancellationPolicy(bookingId, controller.signal)
      .then(setPolicy)
      .catch(() => undefined);
    return () => controller.abort();
  }, [bookingId]);

  /*
   * The window depends on the clock as well as the stage, so it is recomputed
   * whenever the sheet opens on a different booking rather than memoised for
   * the life of the component - a sheet left open across the six-hour
   * boundary would otherwise keep quoting the free window it opened with.
   */
  const window = booking ? windowFor(booking.stage, booking.slotStartAt) : null;
  const estimate = booking && window ? refundAmount(booking, window) : null;
  const reasons = window ? reasonsFor(window) : [];

  // The server wins wherever it has answered.
  const back = policy
    ? policy.requiresOps
      ? null
      : Number(policy.refundAmount)
    : estimate;
  const fee = policy
    ? Number(policy.feeAmount)
    : window && booking && window.feePercent > 0
    ? feeFor(window, booking.payment.paid)
    : 0;
  const outcome =
    policy && !policy.requiresOps ? policy.reason : window?.outcome;

  return (
    <BottomSheet
      visible={booking !== null}
      onClose={onClose}
      heightRatio={0.8}
      footer={
        <View style={styles.footer}>
          <Button
            label={
              window?.key === 'E' ? 'Request to stop work' : 'Cancel booking'
            }
            variant="danger"
            onPress={() =>
              booking ? onConfirm(booking, reason ?? undefined) : undefined
            }
          />
          {/* The way out is the calmer control, and it is the one under the
              thumb - most people who open this sheet do not go through with
              it. */}
          <Button
            label="Keep my booking"
            variant="secondary"
            onPress={onClose}
          />
        </View>
      }
    >
      <ScrollView
        contentContainerStyle={styles.page}
        showsVerticalScrollIndicator={false}
      >
        <Text variant="title">{window?.title}</Text>
        <Text variant="caption" color={colors.inkMuted}>
          {booking?.title} · {booking?.when}
        </Text>

        {/* ---------------------------------------------------- the money */}
        <View style={styles.panel}>
          <View style={styles.panelHead}>
            <Icon name="refund" size={18} color={colors.primary} />
            <Text variant="labelSm" color={colors.inkBody}>
              What you get back
            </Text>
          </View>

          {/*
            A figure where there is one, a sentence where there is not.

            Window E returns null from `refundAmount` on purpose - see the file
            note. Everything else can be quoted to the rupee, and is.
          */}
          {back === null ? (
            <Text variant="bodySm" color={colors.inkBody}>
              {window?.outcome}
            </Text>
          ) : (
            <>
              <Text variant="displaySm">{rupees(back)}</Text>
              <Text variant="caption" color={colors.inkMuted}>
                {outcome}
              </Text>

              {/* The fee in rupees - the server's figure once it has
                  answered, so a flat travel fee shows as what it is. */}
              {booking && fee > 0 ? (
                <View style={styles.feeRow}>
                  <Text variant="caption" color={colors.inkMuted}>
                    Paid {rupees(booking.payment.paid)} · fee {rupees(fee)}
                  </Text>
                </View>
              ) : null}

              {/*
                Not "back in 5–7 working days". A refund is sent by our team
                after they check the cancellation - nothing leaves on its own
                (see the backend's cancellation service) - and the bank's 5–7
                days start from then. Promising the clock from now had people
                counting days before anything was sent.
              */}
              {back > 0 ? (
                <Text variant="caption" color={colors.inkMuted}>
                  Our team will send this back to your {booking?.payment.method}
                  . Once sent, it takes 5–7 working days to reach you.
                </Text>
              ) : null}
            </>
          )}

          {/*
            The coins, said BEFORE confirming rather than after.

            Somebody deciding whether to cancel is weighing what they lose, and
            coins spent on the booking are the part they are most likely to
            assume are gone - this is exactly the anxiety that produced the
            support ticket this feature came from. Stated in both windows,
            including window E, because the coins come back on cancellation
            regardless of whether a person still has to decide the rupee split.
          */}
          {booking?.coinsRedeemed ? (
            <View style={styles.feeRow}>
              <Text variant="caption" color={colors.inkMuted}>
                Your {booking.coinsRedeemed} Homingo Coins go straight back to
                your wallet.
              </Text>
            </View>
          ) : null}
        </View>

        {/* Who else this lands on. Only where somebody is actually affected. */}
        {window?.note ? (
          <View style={styles.note}>
            <Icon name="users" size={15} color={colors.inkMuted} />
            <Text variant="caption" color={colors.inkMuted} style={styles.grow}>
              {window.note}
            </Text>
          </View>
        ) : null}

        {/* --------------------------------------------------- the reason */}
        <View style={styles.block}>
          <Text variant="labelSm" color={colors.inkBody}>
            Why are you cancelling?{' '}
            <Text variant="caption" color={colors.inkMuted}>
              Optional
            </Text>
          </Text>

          {reasons.map(item => {
            const on = reason?.key === item.key;
            return (
              <Pressable
                key={item.key}
                accessibilityRole="radio"
                accessibilityState={{ selected: on }}
                accessibilityLabel={item.label}
                onPress={() => setReason(on ? null : item)}
                style={({ pressed }) => [
                  styles.reason,
                  on ? styles.reasonOn : null,
                  pressed ? styles.pressed : null,
                ]}
              >
                <View style={[styles.radio, on ? styles.radioOn : null]}>
                  {on ? (
                    <Icon
                      name="check"
                      size={11}
                      color={colors.onPrimary}
                      strokeWidth={3}
                    />
                  ) : null}
                </View>
                <Text
                  variant="bodySm"
                  color={colors.inkBody}
                  style={styles.grow}
                >
                  {item.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  page: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
    gap: spacing.xs,
  },
  panel: {
    gap: spacing.xs,
    padding: spacing.lg,
    marginTop: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  panelHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  feeRow: {
    marginTop: 2,
  },
  note: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  block: {
    gap: spacing.sm,
    marginTop: spacing.xl,
  },
  reason: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surface,
    ...hairline,
  },
  reasonOn: {
    borderColor: colors.primary,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.borderStrong,
  },
  radioOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  grow: {
    flex: 1,
  },
  footer: {
    gap: spacing.sm,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default CancelSheet;
